import './itemsHorizontalScroll.css';
import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import users from '../../../users';
import {cn, getBackgroundColor, isMobileDevice, isOnlyMobileDevice} from '../../../../utils/utils';

const ItemsHorizontalScroll = ({
                                   children = undefined,
                                   className = '',
                                   activateShadow = true,
                                   onScrollEnd = undefined
                               }) => {
    const darkMode = useSelector(users.selectors.isDarkMode);
    const containerRef = useRef(null);
    const rafRef = useRef(null);

    // UI state (kept minimal and only updated when values actually change)
    const [atStart, setAtStart] = useState(true);
    const [atEnd, setAtEnd] = useState(false);
    const [scrollSmaller, setScrollSmaller] = useState(false);
    const [startedDragging, setStartedDragging] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    const dragStartXRef = useRef(0);
    const dragStartScrollRef = useRef(0);

    const extraPadding = useMemo(() => (isMobileDevice() ? 0 : 60), []);

    // Read DOM and update booleans. Use small tolerance to avoid 1px fractional jitter.
    const updateShadowsFromDOM = useCallback(() => {
        const el = containerRef.current;
        if (!el) return;

        const {scrollLeft, scrollWidth, clientWidth} = el;
        const tolerance = 1; // px

        const newAtStart = scrollLeft <= extraPadding + tolerance;
        const newAtEnd = scrollLeft + clientWidth + extraPadding >= scrollWidth - tolerance;
        const newScrollSmaller = scrollWidth <= clientWidth - 120;

        // Avoid unnecessary state updates to reduce re-renders
        setAtStart(prev => (prev === newAtStart ? prev : newAtStart));
        setAtEnd(prev => (prev === newAtEnd ? prev : newAtEnd));
        setScrollSmaller(prev => (prev === newScrollSmaller ? prev : newScrollSmaller));
    }, [extraPadding]);

    // Use rAF to coalesce scroll events
    const handleScroll = useCallback(() => {
        if (rafRef.current != null) return;
        rafRef.current = requestAnimationFrame(() => {
            updateShadowsFromDOM();
            if (onScrollEnd && containerRef.current) {
                const {scrollLeft, scrollWidth, clientWidth} = containerRef.current;
                if (scrollLeft + clientWidth >= scrollWidth - 20) {
                    onScrollEnd();
                }
            }
            rafRef.current = null;
        });
    }, [onScrollEnd, updateShadowsFromDOM]);

    // Layout effect ensures we set initial scroll position before paint
    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        // If content overflows, set initial offset synchronously to avoid 1px jump flicker
        if (el.scrollWidth > el.clientWidth) {
            el.scrollLeft = extraPadding;
        }

        const {scrollLeft, scrollWidth, clientWidth} = containerRef.current;
        if (scrollLeft + clientWidth >= scrollWidth - 20) {
            onScrollEnd?.();
        }
        updateShadowsFromDOM();

        // Listen to scroll and size changes
        el.addEventListener('scroll', handleScroll, {passive: true});

        const ro = new ResizeObserver(() => {
            updateShadowsFromDOM();
        });
        ro.observe(el);

        // Also listen to window resize as a fallback
        window.addEventListener('resize', updateShadowsFromDOM);

        return () => {
            el.removeEventListener('scroll', handleScroll);
            ro.disconnect();
            window.removeEventListener('resize', updateShadowsFromDOM);
            if (rafRef.current != null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [extraPadding, handleScroll, onScrollEnd, updateShadowsFromDOM]);

    useEffect(() => {
        // When children change, re-evaluate scroll end
        const el = containerRef.current;
        if (!el) return;
        requestAnimationFrame(() => {
            if (el.scrollLeft === 0 && el.scrollWidth > el.clientWidth) {
                el.scrollLeft = extraPadding;
            }

            const {scrollLeft, scrollWidth, clientWidth} = el;
            if (scrollLeft + clientWidth >= scrollWidth - 20) {
                onScrollEnd?.();
            }
            // and reevaluate shadows
            updateShadowsFromDOM();
        });
    }, [children, onScrollEnd, updateShadowsFromDOM]);

    const scrollRight = () => {
        const el = containerRef.current;
        if (!el) return;
        const amount = Math.max(0, el.clientWidth - 250);
        el.scrollBy({left: amount, behavior: 'smooth'});
    };

    const scrollLeft = () => {
        const el = containerRef.current;
        if (!el) return;
        const amount = Math.max(0, el.clientWidth - 250);
        el.scrollBy({left: -amount, behavior: 'smooth'});
    };

    const bg = getBackgroundColor(darkMode);
    const paddingValue = ((atStart && atEnd) || scrollSmaller) ? 0 : extraPadding;

    // Drag handlers solo para desktop
    const onMouseDown = useCallback((e) => {
        if (isOnlyMobileDevice() || e.button !== 0) return;
        setStartedDragging(true);
        dragStartXRef.current = e.clientX;
        dragStartScrollRef.current = containerRef.current.scrollLeft;
    }, []);

    const onMouseMove = useCallback((e) => {
        if (!startedDragging) return;
        setStartedDragging(true);
        setIsDragging(true);
        const el = containerRef.current;
        if (!el) return;
        const dx = dragStartXRef.current - e.clientX;
        el.scrollLeft = dragStartScrollRef.current + dx;
        updateShadowsFromDOM();
    }, [startedDragging, updateShadowsFromDOM]);

    const onMouseLeave = useCallback(() => {
        setStartedDragging(false);
        if (isDragging) {
            setIsDragging(false);
        }
    }, [startedDragging]);

    const onMouseUp = useCallback(() => {
        setStartedDragging(false);
        if (isDragging) {
            setIsDragging(false);
        }
    }, [isDragging]);

    useLayoutEffect(() => {
        if (!containerRef.current) return;
        const el = containerRef.current;
        // Solo desktop
        if (!isOnlyMobileDevice()) {
            el.addEventListener('mousedown', onMouseDown);
            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);
            el.addEventListener('mouseleave', onMouseLeave);
        }
        return () => {
            if (!isOnlyMobileDevice()) {
                el.removeEventListener('mousedown', onMouseDown);
                window.removeEventListener('mousemove', onMouseMove);
                window.removeEventListener('mouseup', onMouseUp);
                el.removeEventListener('mouseleave', onMouseLeave);
            }
        };
    }, [onMouseDown, onMouseLeave, onMouseMove, onMouseUp]);

    return (
        <div className={cn('items-horizontal-scroll', className)} style={{position: 'relative', width: '100%'}}>
            {/* Shadows/buttons: hidden when shadows disabled or on mobile */}
            <div hidden={isOnlyMobileDevice()} className="items-horizontal-scroll__right-shadow"
                 style={{
                     opacity: atEnd ? 0 : 1,
                     background: activateShadow ? `linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, ${bg} 100%)` : 'none'
                 }}>
                <button hidden={atEnd} onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    scrollRight();
                }}>
                    <i className={cn("fa-solid fa-chevron-right", {"items-horizontal-scroll_backdrop-btn": !activateShadow})}></i>
                </button>
            </div>

            <div hidden={isOnlyMobileDevice()} className="items-horizontal-scroll__left-shadow"
                 style={{
                     opacity: atStart ? 0 : 1,
                     background: activateShadow ? `linear-gradient(-90deg, rgba(255, 255, 255, 0) 0%, ${bg} 100%)` : 'none'
                 }}>
                <button hidden={atStart} onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    scrollLeft();
                }}>
                    <i className={cn("fa-solid fa-chevron-left", {"items-horizontal-scroll_backdrop-btn": !activateShadow})}></i>
                </button>
            </div>

            <div ref={containerRef} className='horizontal-scroll__scroll-container' style={{
                display: 'flex',
                flexDirection: 'row',
                overflowX: 'auto',
                overflowY: 'hidden',
                scrollbarWidth: 'none', /* Firefox */
                msOverflowStyle: 'none',  /* IE 10+ */
                paddingLeft: `${paddingValue}px`,
                paddingRight: `${paddingValue}px`,
                cursor: isDragging ? 'grabbing' : 'grab',
                position: 'relative', // Para overlay absoluto
                paddingTop: '10px',
                gap: '1rem',
            }}>
                {isDragging && (
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100%',
                            bottom: 0,
                            zIndex: 10,
                            background: 'transparent',
                            pointerEvents: 'all',
                        }}
                    />
                )}
                {children}
            </div>
        </div>
    );
};

export default ItemsHorizontalScroll;
